import z from 'zod'
import { Model } from '../../_kernel/architecture'
import { ID } from '../../_kernel/ID'
import { ListOfFacility } from '../../shared/domain/ListOfFacility'
import { ListOfImage } from '../../_kernel/ListOfImage'
import { Name } from '../../_kernel/Name'
import { Pax } from '../../shared/domain/Pax'
import { Allotment } from './Allotment'

export const RoomValidation = z.object({
  id: z.instanceof(ID, {
    message: '[Room] id is required',
  }),
  name: z.instanceof(Name, { message: '[Room] name is required' }),
  pax: z.instanceof(Pax, { message: '[Room] pax is required' }),
  allotment: z.instanceof(Allotment, { message: '[Room] allotment is required' }),
  facilities: z.instanceof(ListOfFacility).optional(),
  images: z.instanceof(ListOfImage).optional(),
})

export class Room extends Model {
  static create({ id, name, pax, allotment, facilities, images }: z.infer<typeof RoomValidation>) {
    RoomValidation.parse({ id, name, pax, allotment, facilities, images })
    return new Room(
      id,
      name,
      pax,
      allotment,
      facilities ?? ListOfFacility.empty(),
      images ?? ListOfImage.empty()
    )
  }

  static fromJSON(
    json: ReturnType<Room['toJSON']> & {
      name: string
      facilities?: ReturnType<ListOfFacility['toJSON']>
      images?: ReturnType<ListOfImage['toJSON']>
    }
  ) {
    return Room.create({
      id: ID.create({ value: json.id }),
      name: Name.create({ value: json.name }),
      pax: Pax.fromJSON(json.pax),
      allotment: Allotment.create({ value: json.allotment }),
      facilities: json.facilities
        ? ListOfFacility.fromJSON({ json: json.facilities })
        : ListOfFacility.fromJSONOrEmpty({ json: undefined }),
      images: json.images
        ? ListOfImage.fromJSON({ json: json.images })
        : ListOfImage.create({ list: [] }),
    })
  }

  constructor(
    private readonly _id: ID,
    private readonly _name: Name,
    private readonly _pax: Pax,
    private readonly _allotment: Allotment,
    private readonly _facilities: ListOfFacility,
    private readonly _images: ListOfImage
  ) {
    super()
  }

  roomID() {
    return this._id
  }

  name() {
    return this._name.value
  }

  pax() {
    return this._pax
  }

  adults() {
    return this.pax().adultsCount()
  }

  isMoreThanOneAdult() {
    return this.pax().isMoreThanOneAdult()
  }

  children() {
    return this.pax().childrenCount()
  }

  isMoreThanOneChild() {
    return this.pax().isMoreThanOneChild()
  }

  isAtLeastOneChild() {
    return this.pax().isAtLeastOneChild()
  }

  allotment() {
    return this._allotment.value
  }

  isLastRoom() {
    return this._allotment.isTheLastOne()
  }

  facilities() {
    return this._facilities
  }

  images() {
    return this._images
  }

  firstImage() {
    return this._images.at(0)
  }

  imagesCount() {
    return this._images.count()
  }

  hasImages() {
    return this.imagesCount() > 1
  }

  toJSON() {
    return {
      id: this._id.value,
      name: this._name.value,
      pax: this._pax.toJSON(),
      allotment: this._allotment.value,
      facilities: this._facilities.toJSON(),
      images: this._images.toJSON(),
    }
  }
}
